import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback
} from 'react';
import { useNotif } from 'src/hooks';
import { useFRcamera } from '../../useAiCamera';

const FaceRegistrationContext = createContext({
  mode: 1,
  aiBox: false,
  aiLandmark: false,
  showGuideSquare: false,
  imgCount: 5,
  webcamRef: null,
  canvasRef: null,
  capturedImage: null,
  setCapturedImage: () => {},
  capture: () => {},
  bioID: '',
  setBioID: () => {},
  faceDetected: {
    face: false,
    details: { confidence: 0, name: '' }
  },
  loadingModels: false,
  registeredFaces: [],
  setRegisteredFaces: () => {},
  handleRegisterFace: () => {},
  handleSaveImages: () => {},
  IDLabel: '',
  setIDLabel: () => {},
  register: false,
  setRegister: () => {},
  data: {}, //From Field component
  onChangeField: () => {}, // From Field component,
  saved: false,
  setSaved: () => {},
  req: {},
  imgs: [],
  handleSetBioID: () => {},
  dataImgs: [],
  setDataImgs: () => {}
});

function FaceRegistrationProvider({ children }) {
  const { onChangeField, settings, data } = useFRcamera();
  const { imgCount = 5 } = data || settings || {};

  const [registeredFaces, setRegisteredFaces] = useState([]);
  const [bioID, setBioID] = useState(null);
  const [saved, setSaved] = useState(false);

  const notiffy = useNotif();
  const webcamRef = useRef(null);
  const canvasRef = useRef(null);

  const compressImageToTargetSize = async (
    input,
    targetKB = 13,
    maxSize = 96
  ) => {
    const targetBytes = targetKB * 1024;

    const loadImage = src =>
      new Promise(resolve => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.src = src;
      });

    const getDataUrl = (img, size, quality) => {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, size, size);

      return canvas.toDataURL('image/jpeg', quality);
    };

    const getSize = dataUrl => Math.ceil((dataUrl.length * 3) / 4);

    let img;

    if (input instanceof File || input instanceof Blob) {
      const reader = new FileReader();
      const base64 = await new Promise(res => {
        reader.onload = e => res(e.target.result);
        reader.readAsDataURL(input);
      });
      img = await loadImage(base64);
    } else {
      img = await loadImage(input);
    }

    let size = maxSize;
    let quality = 0.7;
    let output = getDataUrl(img, size, quality);

    while (getSize(output) > targetBytes && quality > 0.1) {
      quality -= 0.1;

      if (quality <= 0.2 && size > 48) {
        size -= 8;
        quality = 0.7;
      }

      output = getDataUrl(img, size, quality);
    }

    return output;
  };

  const processImages = async files => {
    const compressed = [];

    for (let i = 0; i < files.length; i++) {
      const img = await compressImageToTargetSize(files[i], 13.1, 225);
      compressed.push(img);
    }

    return compressed.slice(0, 5);
  };

  const handleSaveImages = async () => {
    const compressedImages = await processImages(registeredFaces);

    onChangeField({
      target: {
        name: data.fld,
        value: {
          id: bioID,
          images: compressedImages
        }
      }
    });

    setSaved(true);
  };

  const handleRegisterFace = useCallback(() => {
    const maxImages = imgCount || 5;

    const img = webcamRef.current?.getScreenshot();
    if (!img) return;

    setRegisteredFaces(prev => {
      if (prev.length >= maxImages) {
        notiffy.warning(`Already captured ${maxImages} images.`);
        return prev;
      }

      const updated = [...prev, img];

      if (updated.length === maxImages) {
        notiffy.success('Face registration complete!');
      }

      return updated;
    });
  }, [imgCount, notiffy]);

  return (
    <FaceRegistrationContext.Provider
      value={{
        webcamRef,
        canvasRef,
        handleRegisterFace,
        registeredFaces,
        handleSaveImages,
        bioID,
        setBioID,
        data,
        imgCount
      }}
    >
      {children}
    </FaceRegistrationContext.Provider>
  );
}

export const useFaceRegistration = () => {
  const ctx = useContext(FaceRegistrationContext);
  if (!ctx) throw new Error('useFRcamera must be used within provider');
  return ctx;
};

export default FaceRegistrationProvider;
